import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Share2, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { getBlogPostBySlug, blogPosts } from '../data/blog';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const shareUrl = `https://webgrowthstudio.com/blog/${post.slug}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: shareUrl,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  // Get related posts (exclude current)
  const relatedPosts = blogPosts.filter(p => p.id !== post.id).slice(0, 2);

  // Custom components for ReactMarkdown
  const markdownComponents = {
    h2: ({ children }) => (
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-16 mb-6 pb-3 border-b-2 border-cyan-100">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mt-10 mb-4">
        {children}
      </h3>
    ),
    p: ({ children }) => {
      // Check if this is a highlighted quote (starts with > in content)
      const text = String(children);
      if (text.startsWith('💡 ') || text.startsWith('⚡ ')) {
        return (
          <div className="my-8 p-6 bg-cyan-50 border-l-4 border-cyan-500 rounded-r-lg">
            <p className="text-lg text-gray-800 font-medium leading-relaxed m-0">
              {text.replace(/^[💡⚡]\s*/, '')}
            </p>
          </div>
        );
      }
      return (
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          {children}
        </p>
      );
    },
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    ul: ({ children }) => (
      <ul className="my-6 space-y-3">
        {children}
      </ul>
    ),
    li: ({ children }) => (
      <li className="flex items-start gap-3 text-lg text-gray-700">
        <span className="flex-shrink-0 w-5 h-5 mt-1 bg-cyan-100 rounded-full flex items-center justify-center">
          <Check className="w-3 h-3 text-cyan-600" />
        </span>
        <span className="leading-relaxed">{children}</span>
      </li>
    ),
    hr: () => (
      <hr className="my-12 border-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-8 p-6 bg-gray-50 border-l-4 border-cyan-500 rounded-r-lg italic">
        <div className="text-lg text-gray-700 leading-relaxed">
          {children}
        </div>
      </blockquote>
    ),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={post.metaTitle}
        description={post.metaDescription}
        keywords={`${post.category}, web design, digital marketing, ${post.title.toLowerCase()}`}
        url={`https://webgrowthstudio.com/blog/${post.slug}`}
        image={post.image}
        type="article"
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-cyan-500 hover:text-cyan-600 mb-8 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
          
          <span className="inline-block px-4 py-1.5 bg-cyan-100 text-cyan-700 text-sm font-semibold rounded-full mb-6">
            {post.category}
          </span>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 leading-tight tracking-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-gray-500 text-sm sm:text-base">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
              {new Date(post.date).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
              {post.readTime}
            </span>
            <button 
              onClick={handleShare}
              className="flex items-center gap-2 text-cyan-500 hover:text-cyan-600 transition-colors sm:ml-auto font-medium"
            >
              <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
              Share
            </button>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-56 sm:h-72 lg:h-80 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <ReactMarkdown components={markdownComponents}>
            {post.content}
          </ReactMarkdown>
        </div>
      </article>

      {/* Divider before Author */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      </div>

      {/* Author Box */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 sm:p-8 border border-gray-100">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left">
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                <span className="text-white font-bold text-2xl">WG</span>
              </div>
              <div>
                <p className="text-sm text-cyan-600 font-semibold uppercase tracking-wide mb-1">Written by</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Web Growth Studio</h3>
                <p className="text-gray-600 mb-5 leading-relaxed">
                  We're a team of passionate digital professionals dedicated to helping businesses thrive online. 
                  From stunning websites to strategic social media management, we deliver results that matter.
                </p>
                <Link to="/contact">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-6">
                    Work With Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`}>
                  <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-100 h-full">
                    <span className="inline-block px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-semibold rounded-full mb-4">
                      {relatedPost.category}
                    </span>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 hover:text-cyan-500 transition-colors leading-snug">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{relatedPost.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Clearly Separated */}
      <section className="relative py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <p className="text-cyan-400 font-semibold uppercase tracking-wider mb-4 text-sm">
            Ready to Take Action?
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            Transform Your Website Into a Growth Engine
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            Let's build a website that actually converts visitors into clients. Get a free consultation and discover your growth potential.
          </p>
          <Link to="/contact">
            <Button className="bg-cyan-500 hover:bg-cyan-400 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-400/30 transition-all duration-300">
              Get Your Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
